import React, { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import classes from './MainLayout.module.css';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { ChevronLeftIcon, ChevronRightIcon } from '../assets/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { changeFileSpeed, changePageDetails, setNavigationStartTime } from '../redux/appConfigSlice';
import { getButtonSize, getSpeakingTestFormattedDate } from '../service/utils';
import { savePrecheckLogs } from '../api/authApis';
import { speedtest } from '../service/SpeedTest';

export const MainLayout = () => {
    const {
        appFontSize,
        pageObject,
        testTaker,
        selectedMic,
        appMicAccess,
        isSilent,
        precheckApiError,
        appTheme,
        speakCheckScreenDetails,
        fileSpeed,
        soundCheckScreenAudio,
        silenceMeanValue,
        insperaDetails,
        selectedTaskId,
        role,
    } = useSelector((state: RootState) => state.appConfigSlice);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const isTestPage = location.pathname === '/test';
    const isAssessmentManager = role === 'ASSESSMENT';
    const inPreviewpage = location.pathname.includes('preview');

    const precheckStartRef = React.useRef<string | null>(null);

    useEffect(() => {
        if (location.pathname.startsWith('/precheck') && !precheckStartRef.current) {
            precheckStartRef.current = new Date().toISOString();
        }
    }, [location.pathname]);

    function causeOfError() {
        let error = null;
        if (selectedMic?.label.trim() === '' && appMicAccess) {
            error = 'Microphone Notfound';
        } else if (isSilent) {
            error = 'Threshold not passed';
        } else if (precheckApiError) {
            error = precheckApiError;
        }
        return error;
    }
    let errorMsg = causeOfError();
    let cmsId = testTaker?.testTakerSessionId;
    let cmsSessionId = cmsId.replace(/[:.;]/g, '_');
    let fileName = `preCheckLog_${cmsSessionId}.txt`;
    const payload = {
        testTakerId: testTaker?.testTakerId,
        cmsSessionId: testTaker?.testTakerSessionId,
        centerName: testTaker?.testTakerCenterName,
        language: testTaker?.testTakerLanguage,
        status: 'Prechecks completed',
        productType: insperaDetails.product === 'CRELLA' ? 'GT' : insperaDetails.product,
        fileName: fileName,
        product: insperaDetails?.product,
        partner: insperaDetails?.partner,
        assessmentId: insperaDetails?.assessmentId,
        externalId: insperaDetails?.externalId,
        uttId: insperaDetails?.globalReferenceId,
        testDate: getSpeakingTestFormattedDate().utc,
        testCentreTimeZone: getSpeakingTestFormattedDate().localTimezone,
        preCheckLog: {
            fontSize: appFontSize,
            contrast: appTheme,
            speckerCheckScreenAudio: soundCheckScreenAudio ? 'played' : 'not-played',
            micCheck: selectedMic?.label,
            uploadSpeed: fileSpeed?.uploadSpeed,
            downloadSpeed: fileSpeed?.downloadSpeed,
            thresholdValue: silenceMeanValue,
            filePathOfRecording: speakCheckScreenDetails?.responseFilePath,
            duration: speakCheckScreenDetails?.duration,
            error: errorMsg,
        },
    };

    useEffect(() => {
        const runSpeedTest = async () => {
            try {
                const results = await speedtest({
                    skipUpload: false,
                    onProgress: (progress: any) => {
                        // Optional: Log progress for debugging
                        if (progress.speed && (progress.phase === 'download' || progress.phase === 'upload')) {
                            console.log(`${progress.type}: ${progress.speed} Mbps`);
                        }
                        if (progress.message) {
                            console.log(progress.message);
                        }
                    },
                });

                // Store results in Redux
                dispatch(
                    changeFileSpeed({
                        downloadSpeed: (results as any).download,
                        uploadSpeed: (results as any).upload,
                    }),
                );
            } catch (error) {
                console.error('Speed test failed:', error);
            }
        };

        runSpeedTest();
    }, [dispatch]);

    useEffect(() => {
        if (testTaker?.testTakerSessionId && fileSpeed?.downloadSpeed && fileSpeed?.uploadSpeed) {
            savePrecheckLogs(testTaker, {
                ...payload,
                status: 'Speed test completed',
            });
        }
    }, [testTaker?.testTakerSessionId, fileSpeed?.downloadSpeed, fileSpeed?.uploadSpeed]);

    const onClickNext = () => {
        if (pageObject.pageIndex === 2 && selectedMic?.label && !pageObject.soundSelection) {
            dispatch(changePageDetails({ ...pageObject, isMicSelected: true }));
            return;
        }

        if (selectedTaskId && isAssessmentManager) {
            return navigate('/test');
        };

        if (pageObject.pageIndex === 3 && pageObject.isSpeakCheckDone && isAssessmentManager) { 
            return navigate('/preview');
        }

        if (pageObject.pageIndex < 4) {
            return dispatch(changePageDetails({ ...pageObject, pageIndex: pageObject.pageIndex + 1 }));
        }

        if (pageObject.isSpeakCheckDone) {
            savePrecheckLogs(testTaker, {
                ...payload,
                recordingStartTimestamp: precheckStartRef.current,
                recordingEndTimestamp: new Date().toISOString(),
            });
        }

        dispatch(setNavigationStartTime(Date.now()));
        return navigate('/test');
    };
    const onClickPrev = () => {
        if (pageObject.pageIndex === 3 && pageObject.isSpeakCheckDone) {
            dispatch(changePageDetails({ ...pageObject, isSpeakCheckDone: false }));
        }
        if (pageObject.pageIndex === 2 && (pageObject.isMicSelected || pageObject.soundSelection)) {
            dispatch(changePageDetails({ ...pageObject, isMicSelected: false, soundSelection: false }));
            return;
        }
        dispatch(changePageDetails({ ...pageObject, pageIndex: pageObject.pageIndex - 1 }));
    };

    return (
        <div className={classes.container}>
            <Header sessionId={testTaker.testTakerSessionId} />
            <main className={classes.main}>
                <section
                    className={classes.content}
                    style={{ justifyContent: !testTaker.testTakerSessionId || isTestPage ? 'center' : undefined }}
                >
                    <Outlet />
                </section>
            </main>
            <Footer>
                {testTaker.testTakerSessionId && isTestPage && isAssessmentManager && (
                    <Button
                        className={classes.prevBtn}
                        iconLeft={ChevronLeftIcon}
                        variant="secondary"
                        size={getButtonSize(appFontSize)}
                        onClick={() => navigate('/preview')}
                    >
                        Back
                    </Button>
                )}
                
                {testTaker.testTakerSessionId && !isTestPage && (
                    <>
                        {pageObject.pageIndex !== 0 && !inPreviewpage && (
                            <Button
                                data-test
                                className={classes.prevBtn}
                                disabled={pageObject.pageIndex === 4 && pageObject.disableSummaryScreenButton}
                                iconLeft={ChevronLeftIcon}
                                variant="secondary"
                                size={getButtonSize(appFontSize)}
                                onClick={onClickPrev}
                            >
                                Previous
                            </Button>
                        )}
                        <Button
                            className={classes.nextBtn}
                            iconRight={ChevronRightIcon}
                            disabled={
                                pageObject.disableSummaryScreenButton ||
                                (pageObject.pageIndex === 3 && !pageObject.isSpeakCheckDone) ||
                                (pageObject.pageIndex === 2 && !pageObject.micListAvailable) ||
                                (pageObject.disableNextButton && isAssessmentManager && inPreviewpage)
                            }
                            size={getButtonSize(appFontSize)}
                            onClick={onClickNext}
                        >
                            Next
                        </Button>
                    </>
                )}
            </Footer>
        </div>
    );
};

MainLayout.displayName = 'MainLayout';
